/* Generatoren der Seitenleiste „Bauen“: Beschreibung der Felder (Beschriftung, Standardwert, Grenzen, Auswahl), Voreinstellungen,
 * Übernahme der Maße aus der Auswahl und der Aufruf der eigentlichen Generatorfunktion (model/gens.ts). Reine Daten und Funktionen. */
import type { BBox, Brush } from './types.ts';
import Gens from './gens.ts';
import type { ArchParams, DomeParams, HintParams, MoundParams, PlaneParams, RingParams, StairsParams, TerrainParams } from './gens.ts';

/** Werte eines Generators: Zahlen, Texte (Texturen, Auswahl) je nach Feld */
export type Params = Record<string, any>;
/** Ein Feld: Schlüssel, Beschriftung (Übersetzungsschlüssel), Standardwert; t = 'tex' (Textur) oder 'sel' (Auswahl mit o), sonst Zahl mit min/max */
export interface GenField { k: string; l: string; v: number | string; t?: 'tex' | 'sel'; min?: number; max?: number; o?: [string, string][] }
export interface GenPreset { n: string; v: Params }
export interface GenSpec {
  id: string; name: string; info: string; f: GenField[]; presets?: GenPreset[];
  /** Prismen-Generator: Ergebnis gehört als func_detail eingefügt (Schalter im Panel, Standard an) */
  detail?: boolean;
  make: (p: Params) => Brush[];
  fromSel: (p: Params, bb: BBox) => void;
}

const TEX = { wall: 'DRKMTLT_WALL', floor: 'CRETE2_FLR03', rock: 'OUT_GRVL1' };
export const GENS: GenSpec[] = [
  {
    id: 'room', name: 'gen.room.name',
    info: 'gen.room.info',
    f: [
      { k: 'x0', l: 'gen.l.ecke_x', v: 0 }, { k: 'y0', l: 'gen.l.ecke_y', v: 0 }, { k: 'z0', l: 'gen.l.ecke_z', v: 0 },
      { k: 'w', l: 'gen.l.breite_x', v: 384, min: 48 }, { k: 'd', l: 'gen.l.tiefe_y', v: 256, min: 48 }, { k: 'h', l: 'gen.l.hoehe_z', v: 160, min: 48 },
      { k: 'wall', l: 'gen.l.wand', v: 16, min: 1 }, { k: 'floor', l: 'gen.l.boden', v: 16, min: 1 }, { k: 'ceil', l: 'gen.l.decke', v: 16, min: 1 },
      { k: 'texWall', l: 'gen.l.wand_tex', t: 'tex', v: TEX.wall }, { k: 'texFloor', l: 'gen.l.boden_tex', t: 'tex', v: TEX.floor }, { k: 'texCeil', l: 'gen.l.decken_tex', t: 'tex', v: TEX.floor },
      { k: 'door', l: 'gen.l.tuer', t: 'sel', v: 'none', o: [['none', 'gen.o.keine'], ['-x', 'gen.o.side_minus_x'], ['+x', 'gen.o.side_plus_x'], ['-y', 'gen.o.side_minus_y'], ['+y', 'gen.o.side_plus_y']] },
      { k: 'doorW', l: 'gen.l.tuer_breite', v: 64, min: 16 }, { k: 'doorH', l: 'gen.l.tuer_hoehe', v: 112, min: 16 }, { k: 'doorO', l: 'gen.l.tuer_versatz', v: 0 }
    ],
    make: (p: Params): Brush[] => Gens.room({ x0: p.x0, y0: p.y0, z0: p.z0, x1: p.x0 + p.w, y1: p.y0 + p.d, z1: p.z0 + p.h, wall: p.wall, floor: p.floor, ceil: p.ceil, texWall: p.texWall, texFloor: p.texFloor, texCeil: p.texCeil, door: { side: p.door, width: p.doorW, height: p.doorH, offset: p.doorO } }),
    fromSel: (p: Params, bb: BBox): void => { p.x0 = bb.min[0]; p.y0 = bb.min[1]; p.z0 = bb.min[2]; p.w = bb.max[0] - bb.min[0]; p.d = bb.max[1] - bb.min[1]; p.h = bb.max[2] - bb.min[2]; }
  },
  {
    id: 'stairs', name: 'gen.stairs.name',
    info: 'gen.stairs.info',
    f: [
      { k: 'x0', l: 'gen.l.start_x', v: 0 }, { k: 'y0', l: 'gen.l.start_y', v: 0 }, { k: 'z0', l: 'gen.l.start_z', v: 0 },
      { k: 'dir', l: 'gen.l.richtung', t: 'sel', v: '+x', o: [['+x', 'gen.o.dir_plus_x'], ['-x', 'gen.o.dir_minus_x'], ['+y', 'gen.o.dir_plus_y'], ['-y', 'gen.o.dir_minus_y']] },
      { k: 'width', l: 'gen.l.breite', v: 64, min: 8 }, { k: 'steps', l: 'gen.l.stufen', v: 8, min: 1, max: 64 },
      { k: 'rise', l: 'gen.l.stufenhoehe', v: 8, min: 1 }, { k: 'run', l: 'gen.l.auftritt', v: 16, min: 1 },
      { k: 'tex', l: 'gen.l.textur', t: 'tex', v: TEX.floor }, { k: 'texTop', l: 'gen.l.tritt_tex', t: 'tex', v: TEX.floor }
    ],
    make: (p: Params): Brush[] => Gens.stairs(p as StairsParams),
    fromSel: (p: Params, bb: BBox): void => { p.x0 = bb.min[0]; p.y0 = bb.min[1]; p.z0 = bb.min[2]; p.width = p.dir[1] === 'x' ? bb.max[1] - bb.min[1] : bb.max[0] - bb.min[0]; }
  },
  {
    id: 'cliff', name: 'gen.cliff.name', detail: true,
    info: 'gen.cliff.info',
    f: [
      { k: 'x0', l: 'gen.l.ecke_x', v: 0 }, { k: 'y0', l: 'gen.l.ecke_y', v: 0 }, { k: 'z0', l: 'gen.l.ecke_z', v: 0 },
      { k: 'sx', l: 'gen.l.groesse_x', v: 256, min: 64 }, { k: 'sy', l: 'gen.l.groesse_y', v: 512, min: 64 }, { k: 'sz', l: 'gen.l.hoehe_z', v: 384, min: 64 },
      { k: 'facing', l: 'gen.l.blick_nach', t: 'sel', v: '-x', o: [['-x', 'gen.o.dir_minus_x'], ['+x', 'gen.o.dir_plus_x'], ['-y', 'gen.o.dir_minus_y'], ['+y', 'gen.o.dir_plus_y']] },
      { k: 'cell', l: 'gen.l.rasterweite', v: 64, min: 24 }, { k: 'ratio', l: 'gen.l.dicke_anteil', v: 0.5, min: 0.1, max: 0.95 },
      { k: 'rough', l: 'gen.l.grobheit', v: 60, min: 0 }, { k: 'fine', l: 'gen.l.feinheit', v: 14, min: 0 }, { k: 'wave', l: 'gen.l.welle', v: 30, min: 0 },
      { k: 'lean', l: 'gen.l.neigung', v: 0.1 }, { k: 'seed', l: 'gen.l.zufall', v: 1 }, { k: 'tex', l: 'gen.l.textur', t: 'tex', v: TEX.rock }
    ],
    make: (p: Params): Brush[] => Gens.cliff({ x0: p.x0, y0: p.y0, z0: p.z0, x1: p.x0 + p.sx, y1: p.y0 + p.sy, z1: p.z0 + p.sz, facing: p.facing, cell: p.cell, ratio: p.ratio, rough: p.rough, fine: p.fine, wave: p.wave, lean: p.lean, seed: p.seed, tex: p.tex }),
    fromSel: (p: Params, bb: BBox): void => { p.x0 = bb.min[0]; p.y0 = bb.min[1]; p.z0 = bb.min[2]; p.sx = bb.max[0] - bb.min[0]; p.sy = bb.max[1] - bb.min[1]; p.sz = bb.max[2] - bb.min[2]; }
  },
  {
    id: 'mound', name: 'gen.mound.name', detail: true,
    info: 'gen.mound.info',
    f: [
      { k: 'cx', l: 'gen.l.mitte_x', v: 0 }, { k: 'cy', l: 'gen.l.mitte_y', v: 0 }, { k: 'z0', l: 'gen.l.boden_z', v: 0 },
      { k: 'radius', l: 'gen.l.radius', v: 256, min: 48 }, { k: 'height', l: 'gen.l.hoehe', v: 128, min: 16 }, { k: 'step', l: 'gen.l.rasterweite', v: 48, min: 24 },
      { k: 'seed', l: 'gen.l.zufall', v: 1 }, { k: 'tex', l: 'gen.l.textur', t: 'tex', v: TEX.rock }
    ],
    make: (p: Params): Brush[] => Gens.mound(p as MoundParams),
    fromSel: (p: Params, bb: BBox): void => { p.cx = Math.round((bb.min[0] + bb.max[0]) / 2); p.cy = Math.round((bb.min[1] + bb.max[1]) / 2); p.z0 = bb.min[2]; p.radius = Math.round(Math.max(bb.max[0] - bb.min[0], bb.max[1] - bb.min[1]) / 2); p.height = Math.max(16, bb.max[2] - bb.min[2]); }
  },
  {
    id: 'terrain', name: 'gen.terrain.name', detail: true,
    info: 'gen.terrain.info',
    f: [
      { k: 'x0', l: 'gen.l.ecke_x', v: 0 }, { k: 'y0', l: 'gen.l.ecke_y', v: 0 }, { k: 'z0', l: 'gen.l.boden_z', v: 0 },
      { k: 'sx', l: 'gen.l.groesse_x', v: 1024, min: 128 }, { k: 'sy', l: 'gen.l.groesse_y', v: 1024, min: 128 },
      { k: 'height', l: 'gen.l.hoehe', v: 160, min: 0 }, { k: 'basin', l: 'gen.l.mulde', v: 0, min: 0 }, { k: 'step', l: 'gen.l.rasterweite', v: 64, min: 32 },
      { k: 'seed', l: 'gen.l.zufall', v: 1 }, { k: 'tex', l: 'gen.l.textur', t: 'tex', v: TEX.rock }, { k: 'texBottom', l: 'gen.l.unterseite_tex', t: 'tex', v: 'NULL' }
    ],
    make: (p: Params): Brush[] => Gens.terrain({ x0: p.x0, y0: p.y0, x1: p.x0 + p.sx, y1: p.y0 + p.sy, z0: p.z0, height: p.height, basin: p.basin, step: p.step, seed: p.seed, tex: p.tex, texBottom: p.texBottom } as TerrainParams),
    fromSel: (p: Params, bb: BBox): void => { p.x0 = bb.min[0]; p.y0 = bb.min[1]; p.z0 = bb.min[2]; p.sx = bb.max[0] - bb.min[0]; p.sy = bb.max[1] - bb.min[1]; }
  },
  {
    id: 'plane', name: 'gen.plane.name', detail: true,
    info: 'gen.plane.info',
    f: [
      { k: 'x0', l: 'gen.l.ecke_x', v: 0 }, { k: 'y0', l: 'gen.l.ecke_y', v: 0 }, { k: 'z0', l: 'gen.l.oberflaeche_z', v: 0 },
      { k: 'sx', l: 'gen.l.groesse_x', v: 1024, min: 64 }, { k: 'sy', l: 'gen.l.groesse_y', v: 1024, min: 64 },
      { k: 'step', l: 'gen.l.rasterweite', v: 64, min: 32 }, { k: 'thick', l: 'gen.l.dicke', v: 128, min: 16 },
      { k: 'tex', l: 'gen.l.textur', t: 'tex', v: TEX.rock }, { k: 'texBottom', l: 'gen.l.unterseite_tex', t: 'tex', v: 'NULL' }
    ],
    make: (p: Params): Brush[] => Gens.plane({ x0: p.x0, y0: p.y0, x1: p.x0 + p.sx, y1: p.y0 + p.sy, z0: p.z0, thick: p.thick, step: p.step, tex: p.tex, texBottom: p.texBottom } as PlaneParams),
    fromSel: (p: Params, bb: BBox): void => { p.x0 = bb.min[0]; p.y0 = bb.min[1]; p.z0 = bb.max[2]; p.sx = bb.max[0] - bb.min[0]; p.sy = bb.max[1] - bb.min[1]; }
  },
  {
    id: 'hint', name: 'gen.hint.name',
    info: 'gen.hint.info',
    f: [
      { k: 'axis', l: 'gen.l.achse', t: 'sel', v: 'x', o: [['x', 'gen.o.axis_x'], ['y', 'gen.o.axis_y']] },
      { k: 'pos', l: 'gen.l.position', v: 0 }, { k: 'a0', l: 'gen.l.quer_von', v: 0 }, { k: 'a1', l: 'gen.l.quer_bis', v: 128 },
      { k: 'z0', l: 'gen.l.boden_z', v: 0 }, { k: 'z1', l: 'gen.l.decke_z', v: 160 },
      { k: 'thick', l: 'gen.l.dicke', v: 8, min: 1 }, { k: 'margin', l: 'gen.l.rahmen', v: 16, min: 0 }
    ],
    make: (p: Params): Brush[] => { const b = Gens.hint(p as HintParams); return b ? [b] : []; },
    fromSel: (p: Params, bb: BBox): void => {
      // Die Öffnung ist die Auswahl: die dünnere der beiden Seiten in der Ebene ist die Plattenachse
      const dx = bb.max[0] - bb.min[0], dy = bb.max[1] - bb.min[1], ax = dx <= dy ? 0 : 1;
      p.axis = ax === 0 ? 'x' : 'y'; p.pos = Math.round((bb.min[ax] + bb.max[ax]) / 2);
      p.a0 = bb.min[1 - ax]; p.a1 = bb.max[1 - ax]; p.z0 = bb.min[2]; p.z1 = bb.max[2];
    }
  },
  {
    id: 'arch', name: 'gen.arch.name',
    info: 'gen.arch.info',
    presets: [
      { n: 'gen.p.rundbogen_frei', v: { style: 'round', W: 128, H: 64, T: 16, D: 32, seg: 12, mode: 'free' } },
      { n: 'gen.p.rundbogen_in_wand', v: { style: 'round', W: 128, H: 64, T: 16, D: 32, seg: 12, mode: 'wall', WW: 320, WH: 192 } },
      { n: 'gen.p.spitzbogen_gotisch', v: { style: 'pointed', W: 96, H: 144, T: 14, D: 32, seg: 12, mode: 'free' } },
      { n: 'gen.p.spitzbogen_in_wand', v: { style: 'pointed', W: 96, H: 144, T: 14, D: 32, seg: 12, mode: 'wall', WW: 288, WH: 256 } },
      { n: 'gen.p.flachbogen_segment', v: { style: 'flat', W: 192, H: 40, T: 20, D: 32, seg: 10, mode: 'free' } },
      { n: 'gen.p.ellipsenbogen', v: { style: 'round', W: 192, H: 48, T: 16, D: 32, seg: 14, mode: 'free' } },
      { n: 'gen.p.giebelbogen_dreieck', v: { style: 'gable', W: 128, H: 64, T: 14, D: 32, seg: 1, mode: 'free' } },
      { n: 'gen.p.tonnengewoelbe_tunnel', v: { style: 'round', W: 192, H: 96, T: 16, D: 384, seg: 16, mode: 'free' } },
      { n: 'gen.p.gotisches_gewoelbe', v: { style: 'pointed', W: 128, H: 192, T: 16, D: 256, seg: 14, mode: 'free' } }
    ],
    f: [
      { k: 'x0', l: 'gen.l.mitte_x', v: 0 }, { k: 'y0', l: 'gen.l.mitte_y', v: 0 }, { k: 'z0', l: 'gen.l.boden_z', v: 0 },
      { k: 'axis', l: 'gen.l.achse', t: 'sel', v: 'x', o: [['x', 'gen.o.oeffnung_in_x'], ['y', 'gen.o.oeffnung_in_y']] },
      { k: 'style', l: 'gen.l.stil', t: 'sel', v: 'round', o: [['round', 'gen.o.rund_ellipse'], ['pointed', 'gen.o.spitzbogen'], ['flat', 'gen.o.flachbogen'], ['gable', 'gen.o.giebel_dreieck']] },
      { k: 'W', l: 'gen.l.oeffnung_breite', v: 128, min: 16 }, { k: 'H', l: 'gen.l.scheitel_hoehe', v: 64, min: 8 },
      { k: 'T', l: 'gen.l.bogenstaerke', v: 16, min: 0 }, { k: 'D', l: 'gen.l.tiefe', v: 32, min: 4 }, { k: 'seg', l: 'gen.l.segmente', v: 12, min: 1, max: 32 },
      { k: 'mode', l: 'gen.l.form', t: 'sel', v: 'free', o: [['free', 'gen.o.nur_bogen'], ['wall', 'gen.o.mit_wand_darum']] },
      { k: 'WW', l: 'gen.l.wandbreite', v: 320, min: 32 }, { k: 'WH', l: 'gen.l.wandhoehe', v: 192, min: 16 },
      { k: 'tex', l: 'gen.l.wand_tex', t: 'tex', v: TEX.wall }, { k: 'texArch', l: 'gen.l.bogen_tex', t: 'tex', v: TEX.floor },
      { k: 'grid', l: 'gen.l.runden_auf', v: 1, min: 0 }
    ],
    make: (p: Params): Brush[] => Gens.arch(p as ArchParams),
    fromSel: (p: Params, bb: BBox): void => {
      p.x0 = Math.round((bb.min[0] + bb.max[0]) / 2); p.y0 = Math.round((bb.min[1] + bb.max[1]) / 2); p.z0 = bb.min[2];
      const sx = bb.max[0] - bb.min[0], sy = bb.max[1] - bb.min[1];
      p.W = p.axis === 'x' ? sx : sy; p.D = p.axis === 'x' ? sy : sx; p.WW = p.W; p.WH = Math.max(16, bb.max[2] - bb.min[2]);
      p.H = Math.max(8, Math.round(p.WH / 2));
    }
  },
  {
    id: 'ring', name: 'gen.ring.name',
    info: 'gen.ring.info',
    presets: [
      { n: 'gen.p.viertelkreis_ecke', v: { rIn: 128, T: 16, H: 160, start: 0, sweep: 90, seg: 8, taper: 0 } },
      { n: 'gen.p.halbrunde_apsis', v: { rIn: 128, T: 16, H: 160, start: 0, sweep: 180, seg: 12, taper: 0 } },
      { n: 'gen.p.bastion_270', v: { rIn: 128, T: 24, H: 192, start: -45, sweep: 270, seg: 18, taper: 0 } },
      { n: 'gen.p.rundturm', v: { rIn: 96, T: 24, H: 384, start: 0, sweep: 360, seg: 16, taper: 0 } },
      { n: 'gen.p.verjuengter_turm', v: { rIn: 96, T: 24, H: 384, start: 0, sweep: 360, seg: 16, taper: -32 } },
      { n: 'gen.p.saeule_rund', v: { rIn: 0, T: 24, H: 160, start: 0, sweep: 360, seg: 12, taper: 0 } },
      { n: 'gen.p.saeule_achteckig', v: { rIn: 0, T: 24, H: 160, start: 22.5, sweep: 360, seg: 8, taper: 0 } },
      { n: 'gen.p.brunnenring', v: { rIn: 48, T: 12, H: 40, start: 0, sweep: 360, seg: 16, taper: 0 } },
      { n: 'gen.p.rohr_tank', v: { rIn: 56, T: 8, H: 256, start: 0, sweep: 360, seg: 20, taper: 0 } }
    ],
    f: [
      { k: 'cx', l: 'gen.l.mitte_x', v: 0 }, { k: 'cy', l: 'gen.l.mitte_y', v: 0 }, { k: 'z0', l: 'gen.l.boden_z', v: 0 },
      { k: 'rIn', l: 'gen.l.innenradius', v: 96, min: 0 }, { k: 'T', l: 'gen.l.staerke', v: 24, min: 1 }, { k: 'H', l: 'gen.l.hoehe', v: 192, min: 4 },
      { k: 'start', l: 'gen.l.start_grad', v: 0 }, { k: 'sweep', l: 'gen.l.winkel_grad', v: 360, min: 5, max: 360 }, { k: 'seg', l: 'gen.l.segmente', v: 16, min: 1, max: 96 },
      { k: 'taper', l: 'gen.l.verjuengung_oben', v: 0 },
      { k: 'tex', l: 'gen.l.aussen_tex', t: 'tex', v: TEX.wall }, { k: 'texIn', l: 'gen.l.innen_tex', t: 'tex', v: TEX.wall },
      { k: 'grid', l: 'gen.l.runden_auf', v: 1, min: 0 }
    ],
    make: (p: Params): Brush[] => Gens.ring(p as RingParams),
    fromSel: (p: Params, bb: BBox): void => {
      p.cx = Math.round((bb.min[0] + bb.max[0]) / 2); p.cy = Math.round((bb.min[1] + bb.max[1]) / 2); p.z0 = bb.min[2];
      const rO = Math.round(Math.max(bb.max[0] - bb.min[0], bb.max[1] - bb.min[1]) / 2);
      p.rIn = Math.max(0, Math.min(p.rIn, rO - 4)); p.T = Math.max(1, rO - p.rIn); p.H = Math.max(4, bb.max[2] - bb.min[2]);
    }
  },
  {
    id: 'dome', name: 'gen.dome.name',
    info: 'gen.dome.info',
    presets: [
      { n: 'gen.p.halbkugel_kuppel_schale', v: { style: 'round', R: 192, H: 192, T: 16, sec: 16, rings: 8, start: 0 } },
      { n: 'gen.p.flachkuppel', v: { style: 'flat', R: 192, H: 64, T: 12, sec: 16, rings: 6, start: 0 } },
      { n: 'gen.p.spitzkuppel', v: { style: 'pointed', R: 128, H: 200, T: 12, sec: 16, rings: 8, start: 0 } },
      { n: 'gen.p.zwiebelturm', v: { style: 'onion', R: 96, H: 220, T: 10, sec: 12, rings: 10, start: 0 } },
      { n: 'gen.p.kegeldach_turmhelm', v: { style: 'cone', R: 112, H: 160, T: 8, sec: 12, rings: 1, start: 0 } },
      { n: 'gen.p.pyramidendach_4_seiten', v: { style: 'cone', R: 128, H: 96, T: 8, sec: 4, rings: 1, start: 45 } },
      { n: 'gen.p.achteckdach', v: { style: 'cone', R: 128, H: 112, T: 8, sec: 8, rings: 1, start: 22.5 } },
      { n: 'gen.p.massive_halbkugel_huegel', v: { style: 'round', R: 192, H: 192, T: 0, sec: 20, rings: 10, start: 0 } },
      { n: 'gen.p.flacher_ellipsoid_huegel', v: { style: 'round', R: 256, H: 96, T: 0, sec: 20, rings: 8, start: 0 } }
    ],
    f: [
      { k: 'cx', l: 'gen.l.mitte_x', v: 0 }, { k: 'cy', l: 'gen.l.mitte_y', v: 0 }, { k: 'z0', l: 'gen.l.basis_z', v: 0 },
      { k: 'style', l: 'gen.l.stil', t: 'sel', v: 'round', o: [['round', 'gen.o.halbkugel_ellipsoid'], ['pointed', 'gen.o.spitzkuppel'], ['flat', 'gen.o.flachkuppel'], ['onion', 'gen.o.zwiebel'], ['cone', 'gen.o.kegel_pyramide']] },
      { k: 'R', l: 'gen.l.radius_basis', v: 192, min: 16 }, { k: 'H', l: 'gen.l.hoehe', v: 192, min: 8 }, { k: 'T', l: 'gen.l.schale_0_massiv', v: 16, min: 0 },
      { k: 'sec', l: 'gen.l.sektoren', v: 16, min: 3, max: 48 }, { k: 'rings', l: 'gen.l.ringe', v: 8, min: 1, max: 24 }, { k: 'start', l: 'gen.l.drehung_grad', v: 0 },
      { k: 'tex', l: 'gen.l.aussen_tex', t: 'tex', v: TEX.wall }, { k: 'texIn', l: 'gen.l.innen_tex', t: 'tex', v: TEX.wall },
      { k: 'grid', l: 'gen.l.runden_auf', v: 0.5, min: 0 }
    ],
    make: (p: Params): Brush[] => Gens.dome(p as DomeParams),
    fromSel: (p: Params, bb: BBox): void => {
      p.cx = Math.round((bb.min[0] + bb.max[0]) / 2); p.cy = Math.round((bb.min[1] + bb.max[1]) / 2); p.z0 = bb.min[2];
      p.R = Math.round(Math.max(bb.max[0] - bb.min[0], bb.max[1] - bb.min[1]) / 2); p.H = Math.max(8, bb.max[2] - bb.min[2]);
    }
  }
];

/** Startwerte aller Generatoren */
export function defaultValues(): Record<string, Params> {
  const out: Record<string, Params> = {};
  GENS.forEach((g) => { out[g.id] = {}; g.f.forEach((f) => { out[g.id][f.k] = f.v; }); });
  return out;
}

/** Eingaben (Texte aus den Feldern) in gültige Werte umrechnen: Zahlen auf min/max begrenzt, ungültige Zahlen = Standardwert */
export function genValues(g: GenSpec, state: Params): Params {
  const out: Params = {};
  g.f.forEach((f) => {
    if (f.t === 'tex' || f.t === 'sel') { out[f.k] = String(state[f.k]); return; }
    let x = Number(state[f.k]); if (isNaN(x)) x = f.v as number;
    if (f.min !== undefined) x = Math.max(f.min, x);
    if (f.max !== undefined) x = Math.min(f.max, x);
    out[f.k] = x;
  });
  return out;
}

/** Anzahl der Brushes, die der Generator mit diesen Eingaben erzeugt; -1 bei ungültigen Werten */
export function genCount(g: GenSpec, state: Params): number {
  try { return g.make(genValues(g, state)).length; } catch (err) { return -1; }
}
